<template>
  <div class="flex-col">
    <div class="mb-5 flex justify-between font-weight-350 font-sans text-base">
      <div v-if="activeFilters.text?.length">
        {{ filterTotalList.length }} {{ $t('jobPlatform.listJobAmountHeader') }}
      </div>
      <JobFilterSortBy
        class="ml-auto"
        :sortByOptions="sortLabelList"
        @setChange="changeSort"
      ></JobFilterSortBy>
    </div>
    <ListJobs
      :jobs="filterTotalList"
      isSinglePage
      :placement="placement"
      :label="false"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  JobSearchAndData,
  ActiveJobFilter,
  Searchable,
} from '~/typesManual/jobTypes'

const nuxtApp = useNuxtApp()

const props = defineProps<{
  activeTotalList: JobSearchAndData[]
  activeFilters: ActiveJobFilter
  placement?: string
}>()

const sortConfig = [
  {
    sortLabel: nuxtApp.$t('jobPlatform.listJobSortNewestJob'),
    sortFunc: (a: JobSearchAndData, b: JobSearchAndData) => {
      if (a.searchable.date <= b.searchable.date) {
        return 1
      }
      return -1
    },
  },
  {
    sortLabel: nuxtApp.$t('jobPlatform.listJobSortOldestJob'),
    sortFunc: (a: JobSearchAndData, b: JobSearchAndData) => {
      if (a.searchable.date <= b.searchable.date) {
        return -1
      }
      return 1
    },
  },
]

const sortLabelList = computed(() => {
  const sortLabelList: string[] = []
  sortConfig.forEach((item) => {
    sortLabelList.push(item.sortLabel)
  })

  return sortLabelList
})

const sortFuncList = computed(() => {
  const sortFuncList: { (a: JobSearchAndData, b: JobSearchAndData): number }[] =
    []
  sortConfig.forEach((item) => {
    sortFuncList.push(item.sortFunc)
  })

  return sortFuncList
})

const activeSortFunction = ref<{
  (a: JobSearchAndData, b: JobSearchAndData): number
}>(sortFuncList.value[0])

const AndMatchRegexToStringList = (
  stringList: string[],
  regexList: RegExp[]
): boolean => {
  if (stringList === undefined) {
    return false
  }

  for (let j = 0; j < regexList.length; j++) {
    const regexpItem = regexList[j]
    let hasMatch = false

    for (let i = 0; i < stringList.length; i++) {
      const aString = stringList[i]

      if (aString.match(regexpItem)) {
        hasMatch = true
        break
      }
    }

    if (!hasMatch) {
      return false
    }
  }
  return true
}

const OrMatchRegexToStringList = (
  stringList: string[],
  regexList: RegExp[]
): boolean => {
  if (stringList === undefined) {
    return false
  }

  let hasMatch = false
  for (let j = 0; j < regexList.length; j++) {
    const regexpItem = regexList[j]

    for (let i = 0; i < stringList.length; i++) {
      const aString = stringList[i]

      if (aString.match(regexpItem)) {
        hasMatch = true
        return hasMatch
      }
    }
  }
  return hasMatch
}

const hasMatch = (
  comparisonType: string,
  filterRegexList: RegExp[],
  itemValueList: string[]
) => {
  if (comparisonType === 'AND') {
    if (!AndMatchRegexToStringList(itemValueList, filterRegexList)) {
      return false
    }
  } else if (comparisonType === 'OR') {
    if (!OrMatchRegexToStringList(itemValueList, filterRegexList)) {
      return false
    }
  }

  return true
}

const matchFilters = (
  item: Searchable,
  activeFilters: ActiveJobFilter
): boolean => {
  const activeSetup = activeFilters['setup']

  if (activeFilters.text) {
    if (!hasMatch(activeSetup.text, activeFilters.text, item.text)) {
      return false
    }
  }
  if (activeFilters.category) {
    if (
      !hasMatch(activeSetup.category, activeFilters.category, item.category)
    ) {
      return false
    }
  }
  if (activeFilters.location) {
    if (
      !hasMatch(activeSetup.location, activeFilters.location, item.location)
    ) {
      return false
    }
  }

  return true
}

const changeSort = (newActiveSortIndex: number) => {
  activeSortFunction.value = sortFuncList.value[newActiveSortIndex]
}

const filterTotalList = computed(() => {
  const tempList: JobSearchAndData[] = []
  for (let i = 0; i < props.activeTotalList.length; i++) {
    const item = props.activeTotalList[i]
    if (matchFilters(item.searchable, props.activeFilters)) {
      tempList.push(item)
    }
  }
  if (activeSortFunction.value) {
    tempList.sort(activeSortFunction.value)
  }

  return tempList.map((item) => item.data)
})
</script>
